import React from 'react';
import loadable from '@loadable/component';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const fallbackTerms = `**TERMS OF SERVICE**  
**FOR**  
**LENDS MARKETPLACE**  
**Last Updated: \[5/8/2026\]**

These Terms of Service (“Terms”) govern user (“you,” “your,” “user,” or similar references) access to and use of Lends’ website, mobile applications, and related services (collectively, the “Platform”). The Platform is an online marketplace that enables users to list, discover, and book (i) physical goods (such as equipment, tools, and furnishings), (ii) spaces (such as studios, event venues, and commercial premises), and (iii) services related to or independent of such goods and spaces (collectively, “Listings”).

The Platform connects users who offer Listings (“Providers”) with users who seek to rent or book such Listings (“Customers”). Lends operates solely as a technology platform and does not own, control, or provide the goods, spaces, or services listed, and is not a party to any agreement between users except as expressly stated in these Terms.

By accessing or using the Platform, you agree to be bound by these Terms and our Privacy Policy. If you do not agree, you must not access or use the Platform.

1. **Acceptance of Terms; Eligibility.**  
     
   1. Acceptance of Terms. By accessing or using the Platform, you agree to be bound by these Terms and all policies referenced herein, including the Privacy Policy (provided here: [/privacy-policy](/privacy-policy)) and any additional policies, guidelines, or rules posted on the Platform from time to time (collectively, the “Agreement”). If you do not agree to the Agreement, you may not access or use the Platform.

   

   2. Eligibility. You must be at least eighteen (18) years old and capable of forming a legally binding contract to use the Platform. By using the Platform, you represent and warrant that you meet these requirements. If you are using the Platform on behalf of a company, organization, or other legal entity, you represent and warrant that you have the authority to bind such entity to this Agreement, and in such case, “you,” “your,” and “user” will refer to that entity.

   

   3. Compliance with Laws. You agree to access and use the Platform in compliance with all applicable laws, rules, and regulations. You may not use the Platform if you are prohibited from doing so under applicable law.

   

   4. Account Requirement. Certain features of the Platform may require you to register for an account. You agree to provide accurate, current, and complete information and to keep such information updated.

   

   5. Account Security. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify Lends immediately of any unauthorized access to or use of your account.

   

   6. Right to Refuse or Terminate Access. Lends reserves the right, in its sole discretion, to suspend, restrict, or terminate your access to the Platform at any time, with or without notice, for any reason, including if you violate this Agreement or if your use of the Platform may create risk or legal exposure for Lends or others.

   

2. **Definitions**. For purposes of these Terms, the following definitions apply:  
     
   1. “Platform” means Lends’ website, mobile applications, and all other related services.

   

   2. “User” means any individual or entity that accesses or uses the Platform.

   

   3. “Provider” means a User who lists or offers any Listing on the Platform, including owners, hosts, or service providers.

   

   4. “Customer” means a User who books, rents, or otherwise obtains a Listing through the Platform.

   

   5. “Listing” means any offering made available through the Platform, including (i) Goods, (ii) Spaces, and/or (iii) Services.

   

   6. “Goods” means tangible personal property made available for temporary use or rental through the Platform.

   

   7. “Spaces” means real property, premises, or portions thereof made available for temporary use, occupancy, or rental through the Platform.

   

   8. “Services” means services offered by Providers through the Platform, whether related to Goods or Spaces or offered independently.

   

   9. “Booking” means a confirmed reservation or transaction between a Provider and a Customer for a Listing.

   

   10. “Rental Period” means the period of time during which a Customer is authorized to use a Listing, as agreed between the Provider and Customer.

   

   11. “Fees” means any amounts charged through the Platform, including rental fees, service fees, deposits, cancellation fees, and any applicable taxes.

   

3. **Nature of the Platform.**  
     
   1. Marketplace Function. The Platform provides an online marketplace that enables Providers to list and offer Listings and enables Customers to discover and book such Listings. Listings may include Goods, Spaces, and Services. Lends facilitates these connections but does not provide, own, lease, manage, control, or deliver any Listings.

   

   2. No Ownership or Control. All Listings are owned, controlled, and provided solely by Providers. Lends does not take possession of or have control over any Goods or Spaces and does not supervise, direct, or control any Services. As a result, Lends does not guarantee the existence, quality, safety, suitability, legality, or condition of any Listing.

   

   3. No Agency or Employment Relationship. Lends is not a party to any agreement between Providers and Customers and does not act as an agent, broker, partner, joint venturer, employer, or representative of any User. Providers act solely on their own behalf and are not employees or agents of Lends.

   

   4. User Responsibility for Transactions. Providers and Customers are solely responsible for:  
        
      1. Negotiating and agreeing to the terms of any Booking;  
      2. Performing their respective obligations; and  
      3. Complying with all applicable laws, rules, and regulations in connection with any Listing or Booking

   

   Any agreement formed in connection with a Booking is solely between the Provider and the Customer.

   

   5. No Endorsement or Verification. Lends does not endorse any User or Listing. While the Platform may offer features such as profiles, ratings, reviews, or verification tools, Lends does not guarantee the accuracy, completeness, or reliability of any information provided by Users.

   

   6. Services Disclaimer. With respect to Services, Lends does not provide, perform, supervise, or control any Services and makes no representations or warranties regarding the quality, timing, legality, or outcome of any Services. Any Services are provided solely by the applicable Provider, who is solely responsible for the performance and results of such Services.

   

   7. Spaces Disclaimer. With respect to Spaces, Lends does not own, lease, or control any real property listed on the Platform. Any use or occupancy of a Space is based solely on the agreement between the Provider and the Customer, and Lends is not a landlord or property manager. Use of Spaces does not create a leasehold or tenancy relationship unless expressly agreed between the Provider and the Customer.

4. **User Accounts.**  
     
   1. Account Registration. To access certain features of the Platform, you will be required to create an account. When registering for an account, you agree to provide accurate, current, and complete information and to keep such information updated at all times. 

   

   2. Account Responsibility. You are responsible for all activity that occurs under your account, whether or not authorized by you. You agree to use the Platform only for lawful purposes and in accordance with these Terms.

   

   3. Account Security. You are responsible for maintaining the confidentiality of your login credentials and for restricting access to your account. You agree to notify Lends immediately of any unauthorized access to or use of your account or any other breach of security.

   

   4. Verification and Background Checks. Lends may, but is not obligated to, verify the identity of Users or conduct background checks. Any verification processes conducted by Lends are for informational purposes only, and Lends does not guarantee the accuracy, completeness, or reliability of any such verification.

   

   5. User Information and Profiles. Users may create profiles, listings, and other content on the Platform. You are solely responsible for the accuracy and completeness of any information you provide, including descriptions of Listings, qualifications, availability, and pricing.

   

   6. Multiple Accounts; Account Transfers. You may not create multiple accounts or transfer your account to another person or entity without Lends’ prior written consent.

   

   7. Suspension and Termination. Lends reserves the right, in its sole discretion, to suspend, restrict, or terminate your account or access to the Platform at any time, with or without notice, for any reason, including if you violate these Terms or if your use of the Platform may create risk or legal exposure for Lends or other Users.

   

   8. No Guarantee of Access. Lends does not guarantee that the Platform or any portion thereof will be available at all times, and may modify, suspend, or discontinue any aspect of the Platform at any time without liability.

   

   9. Communications Monitoring. Lends may monitor, review, retain, or disclose communications made through the Platform for purposes including fraud prevention, safety, enforcement of these Terms, customer support, and legal compliance.

   

5. **Listings & Bookings (General).**  
     
   1. Creation of Listings. Providers may create Listings for Goods, Spaces, and/or Services by submitting information through the Platform. Providers are solely responsible for the accuracy, completeness, and legality of all Listings, including descriptions, pricing, availability, specifications, qualifications, and any applicable terms or conditions. Lends reserves the right, but is not obligated, to review, approve, modify, or remove any Listing at any time, for any reason.

   

   2. Listing Content and Requirements. Providers must ensure that all Listings:   
        
      1. Accurately describe the Goods, Spaces, or Services offered;  
      2. Comply with all applicable laws, regulations, and industry standards; and  
      3. Do not include false, misleading, or deceptive information.

   

   Providers are solely responsible for ensuring that they have all necessary rights, licenses, permits, and authority to offer their Listings.

   

   3. Booking Process. Customers may request or initiate a Booking through the Platform. A Booking is confirmed only when accepted by the Provider or otherwise confirmed through the Platform, as applicable. Once confirmed, a Booking constitutes a binding agreement between the Provider and the Customer, subject to these Terms and any additional terms specified in the Listing.

   

   4. Pricing and Availability. Providers are responsible for setting the pricing, fees, and availability of their Listings, unless otherwise specified by the Platform. Providers must honor the pricing and availability set forth in a confirmed Booking. Lends is not responsible for errors in pricing or availability provided by Providers.

   

   5. Changes and Cancellations by Providers. Providers may not cancel or materially alter a confirmed Booking except in accordance with these Terms or any applicable cancellation policy. Repeated cancellations or failures to honor Bookings may result in penalties, suspension, or removal from the Platform.

   

   6. Customer Obligations. Customers agree to review Listings carefully before booking and are responsible for understanding the terms, requirements, and restrictions associated with any Listing, including use limitations, safety requirements, and any qualifications needed to use Goods, Spaces, or Services.

   

   7. Platform Rights. Lends reserves the right, in its sole discretion, to cancel or refuse any Booking, remove any Listing, or restrict access to the Platform where necessary to prevent fraud, ensure safety, comply with legal obligations, or mitigate risk.

   

   8. No Guarantee of Listings or Bookings. Lends does not guarantee the availability, quality, safety, or suitability of any Listing or that any Booking request will be accepted or fulfilled.

   

6. **Payments & Fees.**  
     
   1. Payment Processing. The Platform may facilitate payments between Customers and Providers through one or more third-party payment processors (“Payment Processors”). By using the Platform, you agree to comply with the terms and conditions of any applicable Payment Processor. Lends is not a bank, money transmitter, or payment processor and does not hold funds for Users, except as may be required to facilitate transactions through Payment Processors.

   

   2. Authorization to Charge. By providing a payment method, you authorize Lends and its Payment Processors to charge your payment method for all Fees incurred in connection with your use of the Platform, including Booking fees, service fees, deposits, cancellation fees, and any applicable taxes.

   

   3. Fees and Commissions. Lends may charge service fees, commissions, or other Fees in connection with the use of the Platform. Applicable Fees will be disclosed prior to completion of a Booking. Unless otherwise stated, Fees paid to Lends are non-refundable.

   

   4. Pricing. Providers are responsible for setting the pricing of their Listings, including any applicable rates, fees, and deposits, unless otherwise specified by the Platform. Customers agree to pay all amounts associated with a confirmed Booking.

   

   5. Security Deposits. Certain Listings may require a security deposit. Deposits may be used to cover damage, loss, late returns, or other amounts owed in connection with a Booking, in accordance with these Terms and any applicable Listing terms. Lends reserves the right to charge or authorize charges against a Customer’s payment method for such amounts, subject to applicable law.

   

   6. Payouts to Providers. Payments to Providers will be processed through the Payment Processor in accordance with its terms and any applicable payout schedule established by Lends. Lends may withhold or delay payouts where necessary to investigate potential fraud, disputes, chargebacks, or violations of these Terms.

   

   7. Taxes. Users are responsible for determining and fulfilling their obligations with respect to applicable taxes, including sales, use, rental, income, or similar taxes arising from their use of the Platform. Where required by law, Lends or its Payment Processors may collect and remit certain taxes on behalf of Users.

   

   8. Refunds and Chargebacks. Refunds are subject to the applicable cancellation policy and these Terms. If a Customer initiates a chargeback or payment dispute, Lends reserves the right to recover the disputed amount and any associated fees from the responsible User, including by offsetting against future payouts or charging the User’s payment method.

   

   9. Currency and Payment Terms. All payments will be processed in the currency displayed on the Platform unless otherwise specified. Additional fees may apply for currency conversion or international transactions.

7. **Cancellation & Refunds.**  
     
   1. Cancellation Policies. Each Listing may be subject to a cancellation policy established by the Provider and/or the Platform (“Cancellation Policy”). The applicable Cancellation Policy will be disclosed to Customers prior to completing a Booking. By confirming a Booking, Customers agree to the applicable Cancellation Policy.

   

   2. Customer Cancellations. Customers may cancel a Booking in accordance with the applicable Cancellation Policy. Any refunds will be determined based on such policy, the timing of the cancellation, and any applicable Fees.

   

   3. Provider Cancellations. Providers are expected to honor all confirmed Bookings. If a Provider cancels a confirmed Booking, the Customer may be entitled to a refund in accordance with these Terms and the applicable Cancellation Policy. Repeated cancellations or failure to fulfill Bookings may result in penalties, reduced visibility, suspension, or removal from the Platform.

   

   4. No-Shows and Failure to Use. If a Customer fails to appear for or utilize a Booking (“No-Show”), refunds, if any, will be determined in accordance with the applicable Cancellation Policy. Providers may be entitled to retain some or all Fees in the event of a No-Show, as specified in the applicable policy.

   

   5. Late Returns and Extensions. Customers are responsible for returning Goods or vacating Spaces at the end of the Rental Period, unless an extension is agreed upon. Late returns or unauthorized extensions may result in additional Fees, as determined by the Provider and/or the Platform.

   

   6. Platform-Initiated Cancellations. Lends reserves the right, in its sole discretion, to cancel any Booking where necessary to prevent fraud, ensure safety, comply with legal obligations, or mitigate risk. In such cases, Lends may, but is not obligated to, provide a refund or credit to the affected User.

   

   7. Service-Specific Considerations. For Listings involving Services, cancellation terms may also take into account preparation time, scheduling commitments, or other factors specific to the nature of the Services, as set forth in the applicable Listing or Cancellation Policy.

   

   8. Non-Refundable Fees. Certain Fees, including Platform service fees, may be non-refundable unless otherwise required by law or expressly stated otherwise.

   

   9. Disputes Regarding Cancellations. Any disputes between Providers and Customers regarding cancellations or refunds are primarily between those parties. Lends may, but is not obligated to, assist in resolving such disputes.

   

8. **Provider Terms.**  
     
   1. General Responsibilities. As a Provider, you are solely responsible for your Listings and for complying with all obligations associated with any Booking. You represent and warrant that you have all rights, authority, and capacity necessary to offer your Listings and to enter into agreements with Customers.

   

   2. Accuracy of Listings. You must ensure that all Listings are accurate, complete, and not misleading, including descriptions, specifications, pricing, availability, and any requirements or restrictions. You are solely responsible for ensuring that Listings accurately reflect the condition, characteristics, and suitability of any Goods, Spaces, or Services offered.

   

   3. Legal Compliance. You agree to comply with all applicable laws, rules, and regulations in connection with your Listings and any Bookings, including those relating to:  
        
      1. Rental or use of Goods;  
      2. Use and occupancy of Spaces (including zoning, permitting, fire codes, and occupancy limits); and  
      3. Provision of Services (including licensing, certification, and professional requirements, where applicable).

   

   4. Safety and Condition. You are responsible for ensuring that all Goods and Spaces are safe, clean, properly maintained, and suitable for their intended use. You must disclose any known defects, hazards, or limitations associated with your Listings and provide any necessary instructions, warnings, or safety information to Customers.

   

   5. Performance of Services. If you offer Services, you are solely responsible for performing such Services in a professional, competent, and lawful manner. You represent and warrant that you possess all required qualifications, licenses, and expertise necessary to provide the Services and that such Services will be performed in accordance with applicable standards.

   

   6. Availability and Fulfillment. You agree to honor all confirmed Bookings and to make Listings available as described. You must provide Customers with any necessary access, instructions, or coordination required to complete the Booking.

   

   7. Risk of Loss and Damage. Except as otherwise provided in these Terms or in any applicable protection program, you are responsible for the condition of your Listings and for determining how risk of loss, damage, or theft is addressed in your Listings. You acknowledge that use of the Platform involves inherent risks, and you assume such risks in offering Listings to Customers.

   

   8. Insurance. You are solely responsible for maintaining any insurance coverage you deem appropriate for your Listings, including coverage for property damage, personal injury, or liability arising from the use of Goods, Spaces, or Services. Lends does not provide insurance coverage for Providers unless expressly stated otherwise.

   

   9. Prohibited Listings. You may not list any Goods, Spaces, or Services that are illegal, unsafe, infringing, or otherwise prohibited under these Terms or applicable law. Lends reserves the right to remove any Listing that violates these Terms or presents risk to Users or the Platform.

   

   10. Taxes. You are solely responsible for determining and fulfilling your obligations with respect to any applicable taxes arising from your Listings or Bookings, including income, sales, use, or similar taxes.

   

   11. Relationship with Platform. You acknowledge that you are an independent user of the Platform and not an employee, agent, partner, or representative of Lends. Nothing in these Terms creates any joint venture, partnership, or employment relationship between you and Lends.

   

9. **Customer Terms.**  
     
   1. General Responsibilities. As a Customer, you are responsible for your use of any Listings and for complying with all obligations associated with any Booking. You agree to use all Listings in a lawful, safe, and responsible manner and in accordance with these Terms and any applicable Listing terms.

   

   2. Review of Listings. You are responsible for reviewing all Listing details, including descriptions, requirements, restrictions, and any applicable Cancellation Policy, prior to confirming a Booking. You acknowledge that Listings are provided by Providers and that Lends does not guarantee their accuracy, quality, or suitability.

   

   3. Use of Goods. You agree to use any Goods only for their intended purpose and in accordance with any instructions, safety guidelines, or restrictions provided by the Provider. You are responsible for ensuring that you have the necessary knowledge, skill, and qualifications to safely use any Goods.

   

   4. Use of Spaces. You agree to use any Spaces in compliance with all applicable laws and any rules or restrictions specified by the Provider, including occupancy limits, noise restrictions, and permitted uses. You are responsible for the conduct of any individuals you invite or allow into a Space and for ensuring that such individuals comply with these Terms.

   

   5. Services. You acknowledge that any Services are provided solely by the applicable Provider and not by Lends. You are responsible for evaluating the suitability, qualifications, and performance of any Provider offering Services and for communicating any requirements or expectations directly with the Provider.

   

   6. Condition, Return, and Access. You agree to return any Goods and vacate any Spaces at the end of the applicable Rental Period in the same condition as received, reasonable wear and tear excepted. You are responsible for any delays, unauthorized extensions, or failure to return or vacate as required.

   

   7. Responsibility for Damage & Loss. You are responsible for any damage to, loss of, or theft of Goods or Spaces arising from your use of a Listing, except to the extent caused by the Provider or as otherwise provided in these Terms. You authorize Lends to charge your payment method for any such amounts, including repair, replacement, cleaning, or related costs, in accordance with these Terms.

   

   8. Assumption of Risk. You acknowledge that the use of Goods, occupancy of Spaces, and receipt of Services may involve inherent risks, including risk of personal injury, property damage, or other harm. To the maximum extent permitted by law, you voluntarily assume all such risks arising from or related to your use of any Listing, including risks associated with entering private property, interacting with third parties, attending events, or using equipment.

   

   9. Prohibited Conduct. You agree not to:  
        
      1. Use any Listing for unlawful or hazardous purposes;  
      2. Misuse, damage, or improperly handle any Goods or Spaces;  
      3. Engage in disruptive, unsafe, or abusive behavior; or  
      4. Violate any rules or requirements specified by the Provider or applicable law.

   

   10. Compliance with Laws. You are solely responsible for complying with all applicable laws, rules, and regulations in connection with your use of any Listing, including any permits, licenses, or approvals required for your intended use.

10. **Service-Specific Terms.**   
      
    1. Independent Services. All Services offered through the Platform are provided solely by Providers. Lends does not provide, perform, supervise, or control any Services and is not responsible for the acts or omissions of any Provider.

    

    2. No Guarantee of Quality or Outcomes. Lends makes no representations or warranties regarding the quality, safety, legality, suitability, or outcome of any Services. Any descriptions, qualifications, or representations regarding Services are provided by the applicable Provider and have not been independently verified by Lends.

    

    3. Provider Responsibility. Providers are solely responsible for the performance of Services, including the quality, timing, legality, and results of such Services. Providers represent and warrant that they possess all necessary skills, qualifications, licenses, and permits required to perform their Services.

    

    4. Customer Responsibility. Customers are solely responsible for evaluating the suitability of any Provider and for communicating their requirements, expectations, and any relevant information necessary for the performance of Services.

    

    5. No Employment or Agency Relationship. Nothing in these Terms creates any employment, agency, partnership, or joint venture relationship between Lends and any Provider. Providers operate as independent contractors and are solely responsible for determining how to perform their Services.

    

    6. Compliance with Laws. Providers and Customers are each responsible for complying with all applicable laws, rules, and regulations relating to the provision and receipt of Services, including any licensing, labor, or regulatory requirements.

    

    7. No Professional Advice. To the extent any Services involve professional or specialized activities, such Services are provided solely by the applicable Provider. Lends does not provide professional advice and disclaims any responsibility for reliance on any Services obtained through the Platform.

11. **Special Provisions for Spaces.**  
      
    1. License to Use; No Tenancy. Any Booking of a Space grants the Customer a limited, temporary, revocable license to use the Space for the agreed-upon purpose and Rental Period. Nothing in these Terms or any Booking creates a lease, tenancy, or other real property interest in favor of the Customer unless expressly agreed in a separate written agreement between the Provider and the Customer.

    

    2. No Landlord or Property Management Role. Lends does not own, lease, manage, or control any Spaces listed on the Platform and is not a landlord, property manager, or real estate broker. All Spaces are provided solely by Providers, who are responsible for the condition, legality, and operation of such Spaces.

    

    3. Compliance with Laws and Requirements. Providers and Customers are each responsible for complying with all applicable laws, rules, and regulations relating to the use of Spaces, including zoning laws, occupancy limits, fire and safety regulations, accessibility requirements, and any permits or licenses required for the intended use.

    

    4. Use Restrictions. Customers must use Spaces only for lawful purposes and in accordance with any rules, restrictions, or conditions specified by the Provider, including those relating to capacity, noise, hours of use, and permitted activities.

    

    5. Responsibility for Conduct. Customers are responsible for their own conduct and the conduct of any guests, invitees, or third parties present in a Space during a Booking. Customers agree to ensure that all such individuals comply with these Terms and any applicable rules or restrictions.

    

    6. Condition and Damage. Customers are responsible for maintaining the condition of the Space during the Rental Period and for any damage, excessive cleaning, or loss caused by the Customer or their guests, reasonable wear and tear excepted.

    

    7. No Guarantee of Suitability. Lends makes no representations or warranties regarding the suitability, safety, or condition of any Space for any particular purpose. Customers are responsible for determining whether a Space meets their needs prior to Booking.

    

    8. Events and Activities. If a Space is used for events, gatherings, or commercial activities, the Customer is solely responsible for obtaining any required permits, licenses, insurance, or approvals and for ensuring compliance with all applicable laws and regulations.

    

    9. Access and Availability. Providers are responsible for providing access to Spaces as described in the Listing. Lends is not responsible for any failure by a Provider to provide access or for interruptions to the availability of a Space.

    10. Surveillance and Recording Devices. Providers are solely responsible for complying with all applicable laws relating to surveillance, monitoring, or recording devices in Spaces and for disclosing the existence and location of any such devices to Customers as required by law. Hidden or undisclosed recording devices are strictly prohibited.

12. **Prohibited Activities.**   
      
    1. General Prohibition. You agree not to use the Platform or any Listing in any manner that violates these Terms or applicable law, or that could harm Lends, its Users, or any third party.

    

    2. Prohibited Uses of the Platform. You agree not to:  
         
       1. Use the Platform for any unlawful, fraudulent, deceptive, or abusive purpose;  
       2. Misrepresent your identity, qualifications, Listings, or intentions;  
       3. Provide false, misleading, or inaccurate information;  
       4. Circumvent or attempt to circumvent the Platform, including by arranging transactions outside of the Platform to avoid Fees;  
       5. Interfere with or disrupt the operation of the Platform or any systems or networks connected to it;  
       6. Access or use the Platform through automated means (including bots, scrapers, or similar tools) without authorization;  
       7. Attempt to gain unauthorized access to any accounts, systems, or data; or  
       8. Engage in any activity that imposes an unreasonable or disproportionately large load on the Platform.

    

    3. Prohibited Conduct in Connection with Listings. You agree not to:  
         
       1. List or offer any Goods, Spaces, or Services that are illegal, unsafe, hazardous, or otherwise prohibited, including, without limitation, any firearms, animals, motorized vehicles that require a license to operate, pharmaceuticals, or any other items that require a license or certification to provide;  
       2. Use any Listing for unlawful, dangerous, or inappropriate purposes;  
       3. Misuse, damage, or improperly handle any Goods or Spaces;  
       4. Engage in behavior that is threatening, abusive, discriminatory, or harmful to others; or  
       5. Violate any rules, restrictions, or requirements associated with a Listing.

    

    4. Circumvention of Platform Protections. You agree not to bypass or undermine any safeguards or policies implemented by Lends, including those relating to payments, bookings, or user verification.

    

    5. Enforcement. Lends reserves the right, in its sole discretion, to investigate and take appropriate action against any User who violates this Section, including suspending or terminating accounts, removing Listings, canceling Bookings, and pursuing any available legal remedies.

    

    6. Non-Circumvention. You agree not to circumvent, avoid, bypass, or undermine the Platform or its Fee structure, including by:  
         
       1. Initiating, soliciting, negotiating, or completing any transaction with another User outside of the Platform that was introduced or facilitated through the Platform;  
       2. Directing or encouraging another User to make or accept payments outside of the Platform; or  
       3. Using the Platform to identify Users and then transacting with them independently of the Platform.

    

    This restriction applies during the period in which you use the Platform and for twelve (12) months thereafter with respect to Users you first connect with through the Platform. Lends reserves the right to charge applicable Fees, recover lost revenue, and/or take enforcement action (including collection costs and reasonable attorneys’ fees), including suspension or termination of your account, if you violate this provision. You authorize Lends to charge your payment method for any Fees that would have been owed had the transaction been completed through the Platform.

    

13. **Content & Listings.**  
      
    1. User Content. The Platform may allow Users to create, upload, post, or otherwise make available content, including Listings, descriptions, reviews, images, and other materials (“User Content”). You are solely responsible for your User Content and represent and warrant that you have all necessary rights, licenses, and permissions to provide such User Content and to grant the rights described in these Terms.

    

    2. License to Platform. By submitting User Content, you grant Lends a worldwide, non-exclusive, royalty-free, sublicensable, and transferable license to use, reproduce, distribute, display, perform, modify, and otherwise exploit such User Content in connection with operating, promoting, and improving the Platform.

    

    3. Content Standards. You agree that your User Content will not:   
         
       1. Be false, misleading, or deceptive;  
       2. Infringe or violate any third-party rights, including intellectual property or privacy rights;  
       3. Be unlawful, abusive, defamatory, or otherwise objectionable; or  
       4. Violate these Terms or applicable law.

    

    4. Platform Rights. Lends reserves the right, but is not obligated, to monitor, review, modify, remove, or disable access to any User Content at any time and for any reason, without notice.

    

    5. No Obligation to Monitor. Lends does not undertake to review all User Content and does not guarantee the accuracy, completeness, or legality of any User Content.

    

    6. Reviews and Feedback. The Platform may allow Users to submit reviews, ratings, or feedback. Such content reflects the views of individual Users and does not represent the views of Lends. Lends reserves the right to remove reviews or feedback that violate these Terms or are otherwise inappropriate.

    

    7. Intellectual Property of Platform. The Platform and all content provided by Lends, excluding User Content, including text, graphics, logos, and software, are owned by or licensed to Lends and are protected by intellectual property laws. You may not copy, reproduce, distribute, or create derivative works from such content without prior written consent from Lends.

    

14. **Disclaimers.**  
      
    1. “As Is” and “As Available”. The Platform is provided on an “as is” and “as available” basis, without warranties of any kind, whether express, implied, or statutory. To the maximum extent permitted by law, Lends disclaims all warranties, including any implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement.

    

    2. No Warranty of Listings. Lends makes no representations or warranties regarding any Listings, including any Goods, Spaces, or Services, or any Provider or Customer. Without limiting the foregoing, Lends does not warrant that any Listing:  
         
       1. Exists or is available;  
       2. Is safe, suitable, or fit for any purpose;  
       3. Will meet your expectations; or  
       4. Complies with applicable laws or regulations.

    

    3. No Warranty of Services. Lends makes no representations or warranties regarding the quality, reliability, timing, legality, or outcome of any Services. Any Services are provided solely by Providers, and Lends disclaims all responsibility for the performance or results of such Services.

    

    4. No Warranty of Spaces. Lends makes no representations or warranties regarding the condition, safety, legality, or suitability of any Space. Use of any Space is at your own risk.

    

    5. No Warranty of Platform Operation. Lends does not warrant that the Platform will be uninterrupted, secure, or error-free, or that any defects will be corrected. Lends does not guarantee the accuracy, reliability, or completeness of any information available on or through the Platform.

    

    6. No Endorsement. Lends does not endorse or recommend any User or Listing. Any interactions between Users are solely at their own risk.

    

    7. Use at Your Own Risk. You acknowledge and agree that your use of the Platform and any Listings is at your own risk. This includes, without limitation, risks arising from the use of Goods, occupancy of Spaces, and receipt of Services, including risks of personal injury, property damage, or other harm.

    

15. **Limitation of Liability.**  
      
    1. Limitation of Liability. To the maximum extent permitted by law, Lends and its affiliates, officers, directors, employees, and agents will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenues, business opportunities, data, or goodwill, arising out of or in connection with these Terms or your use of (or inability to use) the Platform or any Listings, even if Lends has been advised of the possibility of such damages.

    

    2. Cap on Liability. To the maximum extent permitted by law, the total liability of Lends and its affiliates, officers, directors, employees, and agents for any and all claims arising out of or relating to these Terms, the Platform, or any Listings will not exceed the greater of:  
         
       1. The total Fees paid by you to Lends in the twelve (12) months preceding the event giving rise to the claim; or  
       2. One hundred U.S. dollars (USD $100).

    

    3. Platform Role. You acknowledge and agree that Lends is not a party to any agreements between Users and is not responsible for the acts, omissions, or conduct of any User or third party. Lends will not be liable for any damage, loss, injury, death, property damage, or other harm arising out of or related to any Listing, including any Goods, Spaces, or Services.

    

    4. Allocation of Risk. The limitations of liability set forth in this Section reflect a reasonable allocation of risk between you and Lends, and you acknowledge that Lends would not make the Platform available without these limitations.

    

    5. Exceptions. Nothing in these Terms limits or excludes liability that cannot be limited or excluded under applicable law.

16. **Indemnification.**   
      
    1. Indemnification by Users. To the maximum extent permitted by law, you agree to defend, indemnify, and hold harmless Lends and its affiliates, officers, directors, employees, and agents from and against any and all claims, demands, liabilities, damages, losses, costs, and expenses (including reasonable attorneys’ fees) arising out of or related to:  
         
       1. Your use of or access to the Platform;  
       2. Your Listings, Bookings, or interactions with other Users;  
       3. Your violation of these Terms;  
       4. Your violation of any applicable law, rule, or regulation; or  
       5. Your infringement or violation of any third-party rights, including intellectual property, property, or privacy rights.

    

    2. Provider-Specific Indemnification. Without limiting the foregoing, if you are a Provider, you further agree to indemnify and hold harmless Lends from any claims arising out of or related to:  
         
       1. The condition, safety, or legality of any Goods or Spaces you provide;  
       2. The performance, quality, or outcome of any Services you provide;  
       3. Any personal injury, property damage, or other harm arising from your Listings; or  
       4. Your failure to comply with applicable laws, licensing requirements, or regulatory obligations.

    

    3. Customer-Specific Indemnification. Without limiting the foregoing, if you are a Customer, you further agree to indemnify and hold harmless Lends from any claims arising out of or related to:  
         
       1. Your use or misuse of any Listings, including Goods, Spaces, or Services;  
       2. Any damage to or loss of property;  
       3. Any personal injury or harm caused by you or your guests, invitees, or third parties; or  
       4. Your failure to comply with applicable laws or the terms of any Listing.

    

    4. Indemnification Procedures. Lends reserves the right, at its own expense, to assume the exclusive defense and control of any matter subject to indemnification by you, and you agree to cooperate with Lends in the defense of such claims. You may not settle any such claim without Lends’ prior written consent if the settlement imposes any obligation or liability on Lends.

    

17. **Disputes Between Users.**  
      
    1. User Responsibility for Disputes. You acknowledge and agree that any disputes arising between Users, including between Providers and Customers, are solely between those parties. Lends is not a party to such disputes and has no obligation to become involved in or resolve them.

    

    2. Limited Platform Assistance. Lends may, but is not obligated to, assist Users in resolving disputes, including by facilitating communication or providing information related to a Booking. Any such assistance is provided at Lends’ sole discretion and does not create any obligation or liability for Lends.

    

    3. Platform Determinations. In certain circumstances, Lends may make determinations regarding disputes, including with respect to refunds, fees, or enforcement of these Terms. You agree that any such determinations made by Lends are final and binding with respect to your use of the Platform.

    

    4. Release. To the maximum extent permitted by law, you release Lends and its affiliates, officers, directors, employees, and agents from any and all claims, demands, and damages (actual and consequential) arising out of or related to any dispute between Users or any Listing. If you are a California resident, you waive California Civil Code § 1542, which provides:  
       *“A general release does not extend to claims that the creditor or releasing party does not know or suspect to exist in his or her favor at the time of executing the release…”*

    

    5. No Guarantee of Resolution. Lends does not guarantee that any dispute between Users will be resolved and disclaims all liability arising from or related to such disputes.

18. **Termination & Enforcement.**  
      
    1. Termination by User. You may stop using the Platform at any time and may request to close your account in accordance with any procedures provided by Lends.

    

    2. Termination or Suspension by Platform. Lends reserves the right, in its sole discretion, to suspend, restrict, or terminate your access to the Platform, your account, or any Listings at any time, with or without notice, for any reason, including if:  
         
       1. You violate these Terms;  
       2. You engage in conduct that may harm other Users or the Platform;  
       3. You create legal, regulatory, or reputational risk for Lends; or  
       4. Lends is required to do so to comply with applicable law.

    

    3. Effect of Termination. Upon termination:  
         
       1. Your right to access and use the Platform will immediately cease;  
       2. Any Listings you have created may be removed; and  
       3. Any provisions of these Terms that by their nature should survive will remain in effect.

    

    Termination does not relieve you of any obligations incurred prior to termination, including payment obligations or liabilities arising from Bookings.

    

    4. Ongoing Bookings. If your account is terminated or suspended, any existing Bookings may be canceled or completed at Lends’ discretion. Lends may issue refunds, withhold payouts, or otherwise manage such Bookings as it deems appropriate to protect Users or the Platform.

    

    5. Survival. Without limiting the foregoing, the following Sections will survive termination: Sections 14 (Disclaimers), 15 (Limitation of Liability), 16 (Indemnification), 17 (Disputes Between Users), 19 (Privacy and Data Use), and any other provisions that by their nature are intended to survive.

19. **Privacy and Data Use.**  
      
    1. Privacy Policy. Your use of the Platform is subject to Lends’ Privacy Policy, which describes how we collect, use, disclose, and protect your information. By using the Platform, you acknowledge and agree to the terms of the Privacy Policy.

    

    2. Collection and Use of Information. Lends may collect and process information about you in connection with your use of the Platform, including account information, transaction data, communications, and technical data. Such information may be used to operate, maintain, improve, and provide the Platform, facilitate Bookings, process payments, communicate with Users, and enforce these Terms. 

    

    3. Emergency and Safety Disclosures. Lends may disclose information where reasonably necessary to prevent imminent harm, respond to emergencies, or protect the safety of Users or third parties.

    

    4. Sharing of Information Between Users. You acknowledge that, in order to facilitate Bookings, Lends may share certain information between Providers and Customers, including names, contact information, and Booking-related details. You agree to use such information solely for purposes related to the applicable Booking and not for any unauthorized or unrelated purpose.

    

    5. Communications. By using the Platform, you consent to receive communications from Lends, including transactional, operational, and promotional communications, in accordance with applicable law. You may opt out of certain communications as described in the Privacy Policy.

    

    6. Data Security. Lends uses reasonable administrative, technical, and organizational measures to protect your information but cannot guarantee absolute security.

    

    7. Third-Party Services. The Platform may integrate with or rely on third-party services (including Payment Processors). Your use of such services may be subject to their terms and privacy policies.

    

    8. No Guarantee of Data Accuracy. Lends does not guarantee the accuracy or completeness of any information provided by Users.

20. **Governing Law & Dispute Resolution.**  
      
    1. Governing Law. These Terms and any dispute, claim, or controversy arising out of or relating to these Terms or the use of the Platform will be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of laws principles.

    

    2. Agreement to Arbitrate. To the maximum extent permitted by law, any dispute, claim, or controversy arising out of or relating to these Terms, the Platform, or any Listings (collectively, “Disputes”) will be resolved exclusively through binding arbitration, rather than in court, except as otherwise provided herein.

    

    3. Arbitration Procedures. The arbitration will be administered by the American Arbitration Association (AAA) in accordance with its applicable rules. The arbitration will be conducted by a single arbitrator and may be conducted remotely or in a mutually agreed location. The arbitrator will have exclusive authority to resolve any Dispute, including disputes relating to the interpretation, applicability, or enforceability of this arbitration agreement.

    

    4. Exceptions to Arbitration. Notwithstanding the foregoing, either party may bring a claim in small claims court if the claim qualifies. In addition, Lends may seek injunctive or equitable relief in any court of competent jurisdiction to protect its intellectual property or enforce the non-circumvention provisions of these Terms.

    

    5. Class Action Waiver. To the maximum extent permitted by law, you and Lends agree that any Dispute will be conducted solely on an individual basis and not as part of any class, consolidated, or representative action. You waive any right to participate in a class action lawsuit or class-wide arbitration.

    

    6. Jury Trial Waiver. To the extent any Dispute is permitted to proceed in court, you and Lends waive any right to a trial by jury.

    

    7. Venue. If for any reason a Dispute is not subject to arbitration, you agree that such Dispute will be brought exclusively in the state or federal courts located in New York City, New York, and you consent to the personal jurisdiction of such courts.

    

    8. Time Limitations on Claims. To the maximum extent permitted by law, any claim or cause of action arising out of or relating to these Terms or the Platform must be filed within one (1) year after the claim arose, or it will be permanently barred.

21. **General Provisions.**  
      
    1. Entire Agreement. These Terms, together with any policies or documents incorporated by reference (including the Privacy Policy), constitute the entire agreement between you and Lends regarding the Platform and supersede all prior or contemporaneous agreements, communications, and understandings.

    

    2. Severability. If any provision of these Terms is held to be invalid, illegal, or unenforceable, the remaining provisions will remain in full force and effect, and the invalid provision will be modified to the minimum extent necessary to make it enforceable.

    

    3. Waiver. The failure of Lends to enforce any right or provision of these Terms will not constitute a waiver of such right or provision. Any waiver must be in writing and signed by an authorized representative of Lends.

    

    4. Assignment. You may not assign or transfer these Terms, in whole or in part, without the prior written consent of Lends. Lends may assign or transfer these Terms without restriction, including in connection with a merger, acquisition, sale of assets, or by operation of law.

    

    5. Force Majeure. Lends will not be liable for any delay or failure to perform resulting from causes beyond its reasonable control, including acts of God, natural disasters, war, terrorism, labor disputes, failures of utilities or internet services, or governmental actions.

    

    6. Notices. Lends may provide notices to you via email, through the Platform, or by other reasonable means. You agree that such notices will be deemed received when sent or posted. You may provide notices to Lends at \[contact email/address\].

    

    7. Electronic Communications. By using the Platform, you consent to receive electronic communications from Lends, including notices, disclosures, and other communications. You agree that all agreements, notices, and communications provided electronically satisfy any legal requirement that such communications be in writing.

    

    8. Headings. Section headings are for convenience only and have no legal or contractual effect.

    

    9. No Third-Party Beneficiaries. Except as expressly provided in these Terms, nothing in these Terms is intended to confer any rights or remedies on any person other than the parties.`;

export const fallbackSections = {
  sections: [
    {
      sectionType: 'article',
      sectionId: 'terms',
      appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
      title: { fieldType: 'heading1', content: 'Terms of Service' },
      blocks: [
        {
          blockType: 'defaultBlock',
          blockId: 'hero-content',
          text: {
            fieldType: 'markdown',
            content: fallbackTerms,
          },
        },
      ],
    },
  ],
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: 'Terms of Service | Lends Marketplace',
    },
    pageDescription: {
      fieldType: 'metaDescription',
      content: 'Terms of Service for Lends Marketplace.',
    },
  },
};

const FallbackPage = props => {
  return <PageBuilder pageAssetsData={fallbackSections} {...props} />;
};

export default FallbackPage;
